import type { HelpArticle, NavNode } from "./types";

export function buildNavTree(articles: HelpArticle[]): NavNode[] {
  const sorted = [...articles].sort((a, b) => a.nav_order - b.nav_order);
  const byParent = new Map<string | null, HelpArticle[]>();

  for (const a of sorted) {
    const key = a.parent_slug;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(a);
  }

  function buildChildren(parentSlug: string | null, depth: number): NavNode[] {
    const items = byParent.get(parentSlug) || [];
    return items.map((article) => ({
      article,
      children: buildChildren(article.slug, depth + 1),
      depth,
    }));
  }

  return buildChildren(null, 0);
}

export function flattenNavTree(nodes: NavNode[]): NavNode[] {
  const result: NavNode[] = [];
  function walk(nodes: NavNode[]) {
    for (const n of nodes) {
      result.push(n);
      if (n.children.length) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

export function getBreadcrumbs(
  slug: string,
  articles: Map<string, HelpArticle>
): HelpArticle[] {
  const crumbs: HelpArticle[] = [];
  let current = articles.get(slug);
  while (current) {
    crumbs.unshift(current);
    current = current.parent_slug ? articles.get(current.parent_slug) : undefined;
  }
  return crumbs;
}

export function getPrevNext(
  slug: string,
  articles: HelpArticle[]
): { prev: HelpArticle | null; next: HelpArticle | null } {
  const sorted = [...articles].sort((a, b) => a.nav_order - b.nav_order);
  const idx = sorted.findIndex((a) => a.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? sorted[idx - 1] : null,
    next: idx < sorted.length - 1 ? sorted[idx + 1] : null,
  };
}
